// src/index.ts
import { observer } from 'mobx-react-lite';
import { createContext, useContext, useRef } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';

// ----- DI core types -----
export interface ServicesCollectionMap {
  services: IServicesProvider;
}

// В `d.ts` видно, что имена сервисов берутся из ключей ServicesCollectionMap. [file:41][file:43]
export type TServiceNames = keyof ServicesCollectionMap;

// Типы в ваших .d.ts подразумевают шаблон вида InferServiceType<TName>. [file:41][file:45]
export type InferServiceType<TName extends TServiceNames = TServiceNames> = ServicesCollectionMap[TName];

export type TServiceFactoryFn<TName extends TServiceNames = TServiceNames> = () => InferServiceType<TName>;

export interface IServicesProvider {
  requireService<TName extends TServiceNames>(name: TName): InferServiceType<TName>;
}

export interface IServicesCollection extends IServicesProvider {
  addSingleton<TName extends TServiceNames>(name: TName, service: InferServiceType<TName>): this;
  addSingletonFactory<TName extends TServiceNames>(name: TName, createService: TServiceFactoryFn<TName>): this;
  createChildScope(name?: string): IServicesCollection;
}

// ----- Implementation (повторяет поведение из index.mjs) -----
class ServicesCollection implements IServicesCollection {
  private services = new Map<TServiceNames, unknown>();
  private serviceFactories = new Map<TServiceNames, () => unknown>();

  constructor(
    private parentScope: IServicesProvider | null,
    private scopeName: string = `${+new Date()}`,
  ) {}

  requireService<TName extends TServiceNames>(name: TName): InferServiceType<TName> {
    const existing = this.services.get(name);
    if (existing) return existing as InferServiceType<TName>;

    const factory = this.serviceFactories.get(name);
    if (factory) {
      const created = factory();
      this.services.set(name, created);
      return created as InferServiceType<TName>;
    }

    if (this.parentScope) return this.parentScope.requireService(name);

    throw new Error(`Service '${String(name)}' was not registered in ${this.scopeName}`);
  }

  addSingleton<TName extends TServiceNames>(name: TName, service: InferServiceType<TName>): this {
    // В собранном коде есть проверка на повторную регистрацию (хотя там, похоже, опечатка с "name"). [file:42]
    if (this.services.has(name) || this.serviceFactories.has(name)) {
      throw new Error(`Service ${String(name)} was registered in scope ${this.scopeName}`);
    }
    this.services.set(name, service);
    return this;
  }

  addSingletonFactory<TName extends TServiceNames>(name: TName, createService: TServiceFactoryFn<TName>): this {
    if (this.services.has(name) || this.serviceFactories.has(name)) {
      throw new Error(`Service was registered in scope ${this.scopeName}`);
    }
    this.serviceFactories.set(name, createService as unknown as () => unknown);
    return this;
  }

  createChildScope(name?: string): IServicesCollection {
    return new ServicesCollection(this, name);
  }
}

export const globalDIScope: IServicesCollection = new ServicesCollection(null, 'GLOBAL');

// ----- React integration (поведение как в index.mjs) -----
const ScopeContext = createContext<IServicesCollection | null>(null);

// В сборке используется "busy scope" через внешнюю переменную. [file:42]
let currentConnectedScope: IServicesCollection | null = null;

export function connect<TProps, TRef>(renderFn: (props: TProps, ref: TRef) => ReactElement | null): typeof renderFn {
  const name = (renderFn as any).name || 'connectedComponent';

  return {
    [name]: (props: TProps, ref: TRef) => {
      if (currentConnectedScope) throw new Error('Scope is busy');

      currentConnectedScope = useContext(ScopeContext);

      try {
        return renderFn(props, ref);
      } finally {
        currentConnectedScope = null;
      }
    },
  }[name] as typeof renderFn;
}

type ScopeDIProviderProps = PropsWithChildren<{
  name?: string;
  init(parentScope: IServicesProvider): Partial<ServicesCollectionMap>;
}>;

export const ScopeDIProvider = ({ init, name, children }: ScopeDIProviderProps) => {
  const parentScope = useContext(ScopeContext) ?? globalDIScope;
  const ref = useRef<IServicesCollection | null>(null);

  // eslint-disable-next-line react-hooks/refs
  if (!ref.current) {
    const child = parentScope.createChildScope(name);
    const services = init(parentScope);

    for (const [key, value] of Object.entries(services)) {
      child.addSingleton(key as TServiceNames, value as InferServiceType<any>);
    }

    ref.current = child;
  }

  // eslint-disable-next-line react-hooks/refs
  return <ScopeContext.Provider value={ref.current}>{children}</ScopeContext.Provider>;
};

export function requireService<TName extends TServiceNames>(name: TName): InferServiceType<TName> {
  return (currentConnectedScope ?? globalDIScope).requireService(name);
}

export const wrap = <TProps, TRef = undefined>(
  renderFn: (props: TProps, ref: TRef) => ReactElement | null,
): typeof renderFn => {
  const fnName = renderFn.name;
  const x: Record<string, typeof renderFn> = {
    [fnName]: observer(connect(renderFn)),
  };

  return x[fnName];
};

// ----- Module augmentation (как в d.ts) -----
// declare module 'di-ez' {
//   interface ServicesCollectionMap {
//     services: IServicesProvider;
//   }
// }
